import { Typography } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view';
import { SVGIconComponent } from '../../../../ui/components/pure/icons/types';
import { useSettingsTreeNodeStyles } from './settings-tree-node.styles';
import { KnownSettingsNodeDtoStatus, KnownSettingsNodeDtoStyle } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Colors } from '../../../../ui/theme/colors';
import { SettingsNodeStyle } from '../../reducer/settings-tree-reducer.types';
import { CSSProperties } from 'react';

export interface SettingsNodeProps {
  icon: SVGIconComponent;
  label: string;
  id: string;
  parentId: string | null; // Optional property to link to the parent node. null if it's a root node.
  onClick: () => void;
  children?: SettingsNodeProps[];
  isHidden?: boolean; // Optional property to hide nodes in the UI
  isExpandableWhenEmpty?: boolean; // Optional property to control expandability when there are no children
  status?: KnownSettingsNodeDtoStatus;
  style?: SettingsNodeStyle;
  iconStyle?: CSSProperties;
}

export function SettingsTreeNode(props: SettingsNodeProps) {
  const classes = useSettingsTreeNodeStyles();
  if (props.isHidden) {
    return null; // Skip rendering this node if it is hidden
  }

  if (props.isExpandableWhenEmpty) {
    return (
      <TreeItem
        key={props.id}
        classes={{
          content: classes.content,
          label: classes.label
        }}
        itemId={props.id}
        label={
          <SettingsNodeLabel
            icon={props.icon}
            label={props.label}
            onClick={props.onClick}
            status={props.status}
            style={props.style}
            iconStyle={props.iconStyle}
          />
        }
      >
        {/* The Project's child nodes load quite quickly. Showing a loading indicator makes it look jumpy, so we just render and empty child until it loads. */}
        {!props.children?.length ? <span>&nbsp;</span> : undefined}
        {props.children?.map((child) => (
          <SettingsTreeNode key={child.id} {...child} />
        ))}
      </TreeItem>
    );
  }

  return (
    <TreeItem
      key={props.id}
      classes={{
        content: classes.content,
        label: classes.label
      }}
      itemId={props.id}
      label={
        <SettingsNodeLabel
          icon={props.icon}
          label={props.label}
          onClick={props.onClick}
          status={props.status}
          style={props.style}
          iconStyle={props.iconStyle}
        />
      }
    >
      {props.children?.map((child) => (
        <SettingsTreeNode key={child.id} {...child} />
      ))}
    </TreeItem>
  );
}

interface SettingsNodeLabelProps {
  icon: SVGIconComponent;
  label: string;
  onClick: () => void;
  status?: KnownSettingsNodeDtoStatus;
  style?: SettingsNodeStyle;
  iconStyle?: CSSProperties;
}

function SettingsNodeLabel(props: SettingsNodeLabelProps) {
  const classes = useSettingsTreeNodeStyles();

  const computedStyle = props.style ?? getFallbackStyle(props.status);
  const shouldRenderAsLightGrey = computedStyle === KnownSettingsNodeDtoStyle.LightGrey;
  const shouldRenderAsStrikethrough = computedStyle === KnownSettingsNodeDtoStyle.Strikethrough;

  const { width, height } = props.iconStyle ?? {};
  const hasCustomDimensions = width != null || height != null;

  return (
    <div
      className={classes.labelRoot}
      onClick={(event) => {
        event.stopPropagation();
        props.onClick();
      }}
    >
      <props.icon
        className={hasCustomDimensions ? undefined : classes.labelIcon}
        style={{
          color: shouldRenderAsLightGrey ? Colors.midLightGray : Colors.midGray,
          marginRight: 8,
          ...(hasCustomDimensions ? { width, height } : {})
        }}
      />
      <Typography
        variant="body1"
        style={{
          ...(shouldRenderAsLightGrey ? { color: Colors.midLightGray } : {}),
          ...(shouldRenderAsStrikethrough ? { textDecoration: 'line-through' } : {})
        }}
      >
        {props.label}
      </Typography>
    </div>
  );
}

function getFallbackStyle(status?: KnownSettingsNodeDtoStatus): SettingsNodeStyle | undefined {
  switch (status) {
    case KnownSettingsNodeDtoStatus.Inactive:
      return KnownSettingsNodeDtoStyle.LightGrey;
    case KnownSettingsNodeDtoStatus.Deleted:
      return KnownSettingsNodeDtoStyle.Strikethrough;
    default:
      return undefined;
  }
}
